import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  DollarSign,
  PieChart,
  AlertCircle,
  Calendar,
  Award,
  Download,
  FileText
} from 'lucide-react';
import { getRevenueAnalysisReport } from '../../../api/wholesale/reports';
import { exportRevenueAnalysisReport } from '../../../api/wholesale/exports';
import { useFiscalPeriod } from '../../../contexts/FiscalPeriodContext';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const RevenueAnalysisReportPage = () => {
  const { periodRange, getDateRangeParams } = useFiscalPeriod();
  const [reportData, setReportData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    group_by: 'month'
  });

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};

      // Use custom dates if provided, otherwise use fiscal period
      if (filters.date_from || filters.date_to) {
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
      } else {
        const dateParams = getDateRangeParams();
        if (dateParams.start_date) params.date_from = dateParams.start_date;
        if (dateParams.end_date) params.date_to = dateParams.end_date;
        if (dateParams.ignore_global_period) params.ignore_global_period = true;
      }

      if (filters.group_by) params.group_by = filters.group_by;

      const response = await getRevenueAnalysisReport(params);
      setReportData(response?.data || response);
    } catch (err) {
      console.error('Error fetching revenue analysis report:', err);
      setError('Failed to load revenue analysis report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [periodRange]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const handleResetFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      group_by: 'month'
    });
    setTimeout(() => fetchReport(), 100);
  };

  const handleExport = async (format) => {
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.group_by) params.group_by = filters.group_by;

      const response = await exportRevenueAnalysisReport(params, format);

      if (response.success && response.data?.file_url) {
        window.open(response.data.file_url, '_blank');
        alert(`Revenue analysis report exported successfully as ${format.toUpperCase()}`);
      }
    } catch (err) {
      console.error('Error exporting report:', err);
      alert(err.message || 'Failed to export report');
    }
  };

  if (loading && !reportData) {
    return <LoadingSpinner />;
  }

  const monthlyTrend = reportData?.monthly_trend || [];
  const revenueByTier = reportData?.revenue_by_tier || [];
  const receivables = reportData?.outstanding_receivables || {};
  const cashFlow = reportData?.cash_flow_projection || [];
  const profitMargin = reportData?.profit_margin || {};
  const revenueByCompany = reportData?.revenue_by_company || [];

  // Calculate total outstanding by aging
  const totalAging0_30 = parseFloat(receivables.aging_0_30_amount || 0);
  const totalAging31_60 = parseFloat(receivables.aging_31_60_amount || 0);
  const totalAgingOver60 = parseFloat(receivables.aging_over_60_amount || 0);
  const totalAgingSum = totalAging0_30 + totalAging31_60 + totalAgingOver60;

  const tierColorClasses = {
    1: 'bg-amber-600',
    2: 'bg-gray-400',
    3: 'bg-yellow-500',
    4: 'bg-purple-600'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Revenue Analysis Report</h1>
        <p className="text-gray-500 mt-1">Analyze revenue trends, profit margins, and cash flow</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
            <select
              value={filters.group_by}
              onChange={(e) => handleFilterChange('group_by', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Year</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Apply Filters
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Reset
          </button>

          {/* Export Buttons */}
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => handleExport('csv')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              disabled={loading}
            >
              <FileText className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('excel')}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
              disabled={loading}
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Profit Margin Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Total Revenue</p>
            <DollarSign className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ₦{parseFloat(profitMargin.total_revenue || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Total Discounts</p>
            <PieChart className="h-5 w-5 text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ₦{parseFloat(profitMargin.total_discounts || 0).toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {parseFloat(profitMargin.overall_discount_percent || 0).toFixed(1)}% of subtotal
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Avg Discount</p>
            <TrendingUp className="h-5 w-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {parseFloat(profitMargin.avg_discount_percent || 0).toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Monthly Revenue Trend */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-emerald-600" />
          Revenue Trend
        </h2>

        {monthlyTrend.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No trend data available</p>
        ) : (
          <>
            {/* Line Chart Visualization */}
            <div className="mb-6">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === 'revenue' || name === 'subtotal') {
                        return `₦${parseFloat(value).toLocaleString()}`;
                      }
                      return value;
                    }}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#10b981"
                    strokeWidth={2}
                    name="Revenue"
                    dot={{ fill: '#10b981', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="subtotal"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    name="Subtotal"
                    dot={{ fill: '#3b82f6', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Detailed Data Table */}
            <div className="space-y-3">
              {monthlyTrend.map((period, idx) => {
                const maxRevenue = Math.max(...monthlyTrend.map((p) => parseFloat(p.revenue || 0)));
                const widthPercent =
                  maxRevenue > 0 ? (parseFloat(period.revenue || 0) / maxRevenue) * 100 : 0;

                return (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-gray-900">{period.period}</div>
                      <div className="text-sm text-gray-600">{period.order_count} orders</div>
                    </div>
                    <div className="bg-gray-200 rounded-full h-8 relative mb-2">
                      <div
                        className="bg-emerald-500 h-8 rounded-full flex items-center px-4 text-sm font-medium text-white"
                        style={{ width: `${widthPercent}%`, minWidth: '100px' }}
                      >
                        ₦{parseFloat(period.revenue || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-xs text-gray-600">
                      <div>Subtotal: ₦{parseFloat(period.subtotal || 0).toLocaleString()}</div>
                      <div>
                        Discounts: ₦{parseFloat(period.total_discounts || 0).toLocaleString()}
                      </div>
                      <div>Tonnage: {parseFloat(period.total_tonnage || 0).toFixed(2)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Revenue by Tier */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Award className="h-5 w-5 text-emerald-600" />
          Revenue by Tier
        </h2>

        {/* Bar Chart Visualization */}
        {revenueByTier.length > 0 && (
          <div className="mb-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueByTier}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tier_name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => `₦${parseFloat(value).toLocaleString()}`}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend />
                <Bar dataKey="total_revenue" fill="#10b981" name="Revenue" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {revenueByTier.map((tier) => {
            const colorClass = tierColorClasses[tier.tier_level] || 'bg-gray-500';
            const discountAmount = parseFloat(tier.total_discounts || 0);
            const revenue = parseFloat(tier.total_revenue || 0);

            return (
              <div key={tier.tier_level} className={`${colorClass} text-white rounded-lg p-4`}>
                <div className="flex items-center gap-2 mb-2">
                  <Award className="h-5 w-5" />
                  <p className="font-bold">{tier.tier_name}</p>
                </div>
                <p className="text-2xl font-bold mb-1">₦{revenue.toLocaleString()}</p>
                <p className="text-sm opacity-90">{tier.order_count} orders</p>
                <div className="mt-3 pt-3 border-t border-white/30 space-y-1">
                  <p className="text-xs opacity-90">
                    Avg: ₦{parseFloat(tier.avg_order_value || 0).toLocaleString()}
                  </p>
                  <p className="text-xs opacity-90">
                    Discounts: ₦{discountAmount.toLocaleString()} (
                    {tier.default_discount_percentage}%)
                  </p>
                  <p className="text-xs opacity-90">
                    Tonnage: {parseFloat(tier.total_tonnage || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Outstanding Receivables by Aging */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          Outstanding Receivables by Aging
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-500 mb-1">Total Outstanding</p>
            <p className="text-2xl font-bold text-red-600">
              ₦{parseFloat(receivables.total_outstanding || 0).toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">{receivables.total_invoices || 0} invoices</p>
          </div>

          <div className="border border-green-200 bg-green-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-1">0-30 Days</p>
            <p className="text-2xl font-bold text-green-700">₦{totalAging0_30.toLocaleString()}</p>
            <p className="text-xs text-gray-600 mt-1">
              {receivables.aging_0_30_days || 0} invoices
            </p>
          </div>

          <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-1">31-60 Days</p>
            <p className="text-2xl font-bold text-yellow-700">
              ₦{totalAging31_60.toLocaleString()}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {receivables.aging_31_60_days || 0} invoices
            </p>
          </div>

          <div className="border border-red-200 bg-red-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-1">Over 60 Days</p>
            <p className="text-2xl font-bold text-red-700">₦{totalAgingOver60.toLocaleString()}</p>
            <p className="text-xs text-gray-600 mt-1">
              {receivables.aging_over_60_days || 0} invoices
            </p>
          </div>
        </div>

        {/* Aging Visualization */}
        {totalAgingSum > 0 && (
          <>
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-2">Aging Distribution</p>
              <div className="flex h-8 rounded-lg overflow-hidden">
                <div
                  className="bg-green-500 flex items-center justify-center text-xs font-medium text-white"
                  style={{ width: `${(totalAging0_30 / totalAgingSum) * 100}%` }}
                >
                  {((totalAging0_30 / totalAgingSum) * 100).toFixed(0)}%
                </div>
                <div
                  className="bg-yellow-500 flex items-center justify-center text-xs font-medium text-white"
                  style={{ width: `${(totalAging31_60 / totalAgingSum) * 100}%` }}
                >
                  {((totalAging31_60 / totalAgingSum) * 100).toFixed(0)}%
                </div>
                <div
                  className="bg-red-500 flex items-center justify-center text-xs font-medium text-white"
                  style={{ width: `${(totalAgingOver60 / totalAgingSum) * 100}%` }}
                >
                  {((totalAgingOver60 / totalAgingSum) * 100).toFixed(0)}%
                </div>
              </div>
            </div>

            {/* Pie Chart for Aging */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Receivables Aging Breakdown
              </p>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPie>
                  <Pie
                    data={[
                      { name: '0-30 Days', value: totalAging0_30, color: '#10b981' },
                      { name: '31-60 Days', value: totalAging31_60, color: '#eab308' },
                      { name: 'Over 60 Days', value: totalAgingOver60, color: '#ef4444' }
                    ]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {[
                      { name: '0-30 Days', value: totalAging0_30, color: '#10b981' },
                      { name: '31-60 Days', value: totalAging31_60, color: '#eab308' },
                      { name: 'Over 60 Days', value: totalAgingOver60, color: '#ef4444' }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => `₦${parseFloat(value).toLocaleString()}`}
                    contentStyle={{ fontSize: 12 }}
                  />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      {/* Cash Flow Projection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">
          Cash Flow Projection (Next 90 Days)
        </h2>
        <div className="space-y-2">
          {cashFlow.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No cash flow data available</p>
          ) : (
            cashFlow.map((day, idx) => {
              const maxAmount = Math.max(
                ...cashFlow.map((d) => parseFloat(d.expected_amount || 0))
              );
              const widthPercent =
                maxAmount > 0 ? (parseFloat(day.expected_amount || 0) / maxAmount) * 100 : 0;

              return (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-28 text-sm text-gray-600">
                    {new Date(day.due_date).toLocaleDateString()}
                  </div>
                  <div className="flex-1">
                    <div className="bg-gray-200 rounded-full h-6 relative">
                      <div
                        className="bg-blue-500 h-6 rounded-full flex items-center px-3 text-xs font-medium text-white"
                        style={{ width: `${widthPercent}%`, minWidth: '80px' }}
                      >
                        ₦{parseFloat(day.expected_amount || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="w-24 text-sm text-gray-600 text-right">
                    {day.invoice_count} invoices
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Revenue by Company */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Revenue by Manufacturing Company</h2>

        {/* Bar Chart Visualization */}
        {revenueByCompany.length > 0 && (
          <div className="mb-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueByCompany}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="company_name"
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={120}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => `₦${parseFloat(value).toLocaleString()}`}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend />
                <Bar dataKey="total_revenue" fill="#10b981" name="Revenue" radius={[8, 8, 0, 0]} />
                <Bar
                  dataKey="avg_order_value"
                  fill="#3b82f6"
                  name="Avg Order"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Orders
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Avg Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tonnage
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {revenueByCompany.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No data available
                  </td>
                </tr>
              ) : (
                revenueByCompany.map((company, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {company.company_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{company.order_count}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-green-600">
                      ₦{parseFloat(company.total_revenue || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      ₦{parseFloat(company.avg_order_value || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {parseFloat(company.total_tonnage || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalysisReportPage;
